import { type ICheckList, type ICheckListItem } from "../../../interfaces";
import { checkBaseField } from "../../utils/getCheckListProgress";

export enum EChecklistType {
  ALL = "all",
  PROGRESS = "progress",
  COMPLETED = "completed",
}

export const checklistTypes = [
  { label: "All", value: EChecklistType.ALL },
  { label: "In progress / empty", value: EChecklistType.PROGRESS },
  { label: "Completed", value: EChecklistType.COMPLETED },
];
/**
 * Filters the provided checklists based on the specified filter type.
 *
 * @param checklists - An array of checklists to be filtered.
 * @param filter - The filter type to apply (e.g., ALL, PROGRESS, COMPLETED).
 * @returns A new array of filtered checklists with updated visibility flags.
 */
export const filterChecklists = (
  checklists: ICheckList[],
  filter: EChecklistType,
): ICheckList[] => {
  if (filter === EChecklistType.ALL) {
    // Handle ALL filter case separately for single and group checklists
    return checklists.map(checklist => {
      if (checklist.data.type === "single") {
        // For single checklists, ensure no items are hidden
        return {
          ...checklist,
          hidden: false,
        };
      } else if (checklist.data.type === "group") {
        // For group checklists, ensure no groups or items are hidden
        return {
          ...checklist,
          hidden: false,
          data: {
            ...checklist.data,
            list: checklist.data.list.map((groupItem: any) => ({
              ...groupItem,
              hidden: false,
            })),
          },
        };
      }
      // Default case (should not happen if types are correctly defined)
      return checklist;
    });
  }

  const everyCheck = (list: any) =>
    list.every((item: any) => item.unit === "header" || checkBaseField(item));

  return checklists.map(checklist => {
    if (checklist.data.type === "single") {
      // For single-type checklists, set hidden based on the filter condition
      const shouldHide =
        filter === EChecklistType.COMPLETED
          ? !everyCheck(checklist.data.list) // Hide if not all items are checked
          : everyCheck(checklist.data.list); // Hide if all items are checked

      return {
        ...checklist,
        hidden: shouldHide,
      };
    } else if (checklist.data.type === "group") {
      // For group-type checklists, process each item in the group
      const newList = checklist.data.list.map((groupItem: any) => {
        if (!groupItem.items) {
          // If the group item has no items, mark it as hidden
          return {
            ...groupItem,
            hidden: true,
          };
        }

        // Determine if the group item should be hidden
        const shouldHideGroupItem =
          filter === EChecklistType.COMPLETED
            ? !everyCheck(groupItem.items) // Hide if not all items are checked
            : everyCheck(groupItem.items); // Hide if all items are checked

        return {
          ...groupItem,
          hidden: shouldHideGroupItem,
        };
      });

      // Determine if the entire group should be hidden
      const shouldHideGroup = newList.every((item: any) => item.hidden);

      return {
        ...checklist,
        hidden: shouldHideGroup, // Hide the entire group if all items are hidden
        data: {
          ...checklist.data,
          list: newList, // Update the list with hidden flags
        },
      };
    }

    // Default case (should not happen if types are correctly defined)
    return checklist;
  });
};
/**
 * Generates a history record name for a checklist item based on its state changes.
 *
 * @param item - The checklist item to generate the history record name for.
 * @returns A string representing the history record name.
 */
export const getHistoryRecordName = (item: ICheckListItem) => {
  const getValueChangeName = () =>
    item.unit === "text"
      ? "Changed Text"
      : item.value
        ? "Checked"
        : "Unchecked";

  const getAvailabilityChangeName = () =>
    item.notAvailable ? "Selected Not Applicable" : "Selected Applicable";

  if (item.naChanged && !item.changed) return getAvailabilityChangeName();
  else if (item.naChanged && item.changed)
    return `${getAvailabilityChangeName()} and ${getValueChangeName()}`;
  return getValueChangeName();
};

/**
 * Clears specified flags from the provided checklists.
 *
 * @param list - An array of checklists to clear flags from.
 * @param flagsToClear - An array of flag keys to be removed from checklist items.
 * @returns A new array of checklists with the specified flags cleared.
 */
export const clearChecklistsFromFlags = (
  list: ICheckList[],
  flagsToClear: Array<keyof ICheckListItem>,
): ICheckList[] => {
  if (!list) return [];
  const copiedList = structuredClone(list);
  const itemParser = (item: ICheckListItem) => {
    flagsToClear.forEach(flag => {
      if (flag in item) delete item[flag];
    });
    return { ...item, notAvailable: item.notAvailable ?? false };
  };

  return copiedList.map((checklist: any) => {
    if (checklist.data.type === "single") {
      checklist.data.list = checklist.data.list.map(itemParser);
      return checklist;
    } else {
      checklist.data.list = checklist.data.list.map((child: any) => {
        return {
          ...child,
          items: child.items.map(itemParser),
        };
      });
      return checklist;
    }
  });
};
